import {
  applyTimeZoneOffset,
  getIanaTimeZone,
  getTimeZoneOffset,
} from "@shopify/dates";
import { memoize } from "lodash";

export const getDateTimeInShopTimeZone = (date, ianaTimezone) =>
  applyTimeZoneOffset(new Date(date), getIanaTimeZone(), ianaTimezone);

export const getDateInShopTimeZone = (date, ianaTimezone) =>
  applyTimeZoneOffset(date, getIanaTimeZone(), ianaTimezone);

/**
 * Removes browser and shop timezone offset from a date.
 */
export const getDateInUTC = (date, ianaTimezone) =>
  applyTimeZoneOffset(date, ianaTimezone, getIanaTimeZone());

/**
 * Given the shop timezone, returns a timezone offset in hours between the shop timezone and the browser timezone.
 *
 * @param ianaTimezone - Iana
 */
export const getBrowserAndShopTimeZoneOffset = memoize((ianaTimezone) => {
  const browserTimeZone = getIanaTimeZone();

  const timeZoneOffsetMinutes = getTimeZoneOffset(
    new Date(),
    browserTimeZone,
    ianaTimezone
  );

  return timeZoneOffsetMinutes / 60;
});

/**
 * Given a date, returns a copy of the same date at the start of day.
 */
export const getNewDateAtStartOfDay = (date) => {
  const newDate = new Date(date.getTime());
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * Given a date, returns a copy of the same date at the end of day.
 */
export const getNewDateAtEndOfDay = (date) => {
  const newDate = new Date(date.getTime());
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

export const Weekday = Object.freeze({
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
});

export const getBrowserTimezone = () => {
  let method1Result = null;
  let method2Result = null;

  // Method 1: Intl.DateTimeFormat (IANA timezone)
  if (typeof Intl === "object" && Intl.DateTimeFormat) {
    method1Result = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  
  // Method 2: toLocaleString()
  try {
    method2Result = new Date().toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
  } catch (e) {
    // Ignore errors
  }
  
  // If both methods are available, return combined string
  if (method1Result && method2Result) {
    return `${method1Result} (${method2Result})`;
  }
  
  // If only one method is available, return its result
  if (method1Result) return method1Result;
  if (method2Result) return method2Result;
  
  // Method 3: getTimezoneOffset (fallback)
  const offset = new Date().getTimezoneOffset();
  const hours = Math.abs(Math.floor(offset / 60));
  const minutes = Math.abs(offset % 60);
  const sign = offset > 0 ? "-" : "+";
  
  return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

export const DEFAULT_WEEK_START_DAY = Weekday.Sunday;

/**
 * Formats a date/time in a consistent locale-aware format.
 * Example: "25 Nov 2025 — 12:38" or "25 Nov 2025 — 12:38 PM" depending on locale
 * 
 * @param {Date} date - The date to format
 * @param {string} locale - The locale string (e.g., "en-US", "da-DK")
 * @returns {string} Formatted date string
 */
export const formatDateTime = (date, locale = "en-US") => {
  if (!date) return "";
  
  const dateOptions = { day: "numeric", month: "short", year: "numeric" };
  const timeOptions = { hour: "2-digit", minute: "2-digit" };
  
  const datePart = date.toLocaleDateString(locale, dateOptions);
  const timePart = date.toLocaleTimeString(locale, timeOptions);
  
  return `${datePart} — ${timePart}`;
};